import axios from 'axios';

import type { RootState } from '../index';
import type {
  BlogArticle,
  BlogArticleDraft,
  BlogArticleOrigin,
  BlogArticleStatus,
  BlogAuthor,
  BlogCategory,
  BlogFaqItem,
  BlogHero,
  BlogSource,
} from '../../nova/mock-data/types';

/**
 * spark-blog HTTP client + wire mappers for the blog slice (`blog.ts`).
 *
 * The service is a SEPARATE backend from owners-hub: its base URL comes from
 * `window.config.blogApiUrl` and every admin route is tenant-scoped in the path
 * (`{blogApiUrl}/tenants/{tenant_id}/...`). The wire format is snake_case; the
 * TS model (`nova/mock-data/types.ts`) is camelCase, so every payload goes
 * through an explicit field-by-field mapper — unknown wire keys are dropped and
 * server-owned fields (id / status / timestamps) never leave the client.
 *
 * ## Auth is the signed-in owner, not a service token
 *
 * spark-blog's `Admin` scheme is PER-USER: it forwards the `Authorization`
 * header value to owner-hub `/internal/resolve/token`, which answers
 * `{admin, user_id, expires_at}`. So the studio sends the owner's own access
 * token and the service decides what they may touch — an owner reaches only
 * tenants whose `user_id` is theirs (`403` otherwise), an admin reaches all.
 * `window.config.blogApiToken` survives only as a DEV OVERRIDE for pinning
 * someone else's token; production leaves it empty.
 *
 * ## The token goes RAW — no `Bearer ` prefix
 *
 * owner-hub resolves the value as an opaque `user_tokens` primary key (a
 * Prisma `findUniqueOrThrow` on the string it receives) and spark-blog's
 * user-auth middleware forwards the header verbatim, so a `Bearer ` prefix
 * would be looked up as part of the token and never match. This is also every
 * other owner-hub call's idiom in this codebase (`session.ts`,
 * `casinoDomainInfo.ts`). The service's own docs still say "Bearer" — that is
 * left over from the static-token era, whose authenticator stripped the prefix
 * explicitly (`pkg/http/auth.go`, deleted with the per-user auth change).
 *
 * ## The tenant is a UUID
 *
 * `{tenant_id}` is the control-plane tenant UUID. There is no auto-provisioning
 * and no name lookup: an unknown or non-UUID id answers `404 "Tenant not
 * found"` on every content route (it used to list an empty array), so a name
 * like `demo-casino` is no longer a usable value for `blogTenantId`.
 *
 * ## One route here is NOT spark-blog
 *
 * Turning the blog on and off is a control-plane write, and spark-blog accepts
 * those only through its static-token `PUT /tenant` (an owner has no write
 * access to their own tenant at all). owner-hub holds that credential, so the
 * switch is an owner-hub route on `userApiUrl` while its STATE is read back
 * from spark-blog. See {@link setBlogEnabled} / {@link fetchBlogTenantStatus}.
 */

// ── Wire types (snake_case, as the service returns them) ───────────────────

export type ApiBlogHero = { url: string; alt: string; caption: string };

export type ApiBlogFaqItem = { q: string; a: string };

export type ApiBlogSource = { title: string; url: string };

export type ApiBlogAuthorLink = { label: string; url: string };

/**
 * Same omitempty rule as the article (see ApiBlogArticle): a byline the owner
 * left half-filled arrives with only the fields that have a value — the
 * 2026-09-09 payload carried `{links, name, role}` and nothing else.
 * `authorFromApi` fills the rest with ''.
 */
export type ApiBlogAuthor = {
  name?: string;
  role?: string;
  bio?: string;
  avatar_url?: string;
  url?: string;
  links?: ApiBlogAuthorLink[] | null;
};

/**
 * An article as the service serializes it. The service drops every zero-valued
 * field from the JSON (Go `omitempty`): an empty `body_markdown` or
 * `description`, a false `pillar`, a zero `sequence`, a null `hero`/`author`/
 * `category_id`/`published_at` are ABSENT, not null — measured on the
 * spark-blog main on 2026-09-09. So every field the studio can leave empty is
 * optional here, and `articleFromApi` restores the zero value. Only the fields
 * the server itself guarantees stay required: `id`, `slug`, `status` and the
 * timestamps are server-owned, `title` is validated non-empty, and `language`
 * is server-defaulted.
 */
export type ApiBlogArticle = {
  id: string;
  slug: string;
  title: string;
  description?: string;
  status: BlogArticleStatus;
  language: string;
  category_id?: string | null;
  pillar?: boolean;
  sequence?: number;
  body_markdown?: string;
  hero?: ApiBlogHero | null;
  author?: ApiBlogAuthor | null;
  translation_group_id?: string | null;
  meta_title?: string | null;
  question?: string | null;
  at_a_glance?: string | null;
  key_takeaways?: string[] | null;
  faq?: ApiBlogFaqItem[] | null;
  sources?: ApiBlogSource[] | null;
  tags?: string[] | null;
  entities?: string[] | null;
  related_slugs?: string[] | null;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
  /**
   * `studio` or `autopilot`. Optional on the wire for the same reason as the
   * rest: a service build that predates Autopilot sends no such field, and
   * `articleFromApi` reads that as studio-written.
   */
  origin?: BlogArticleOrigin | null;
};

/** Editable subset sent on create/update — the server owns id/status/timestamps. */
export type ApiBlogArticleDraft = {
  slug: string;
  title: string;
  description: string;
  language: string;
  category_id: string | null;
  pillar: boolean;
  sequence: number;
  body_markdown: string;
  hero: ApiBlogHero | null;
  author: ApiBlogAuthor | null;
  translation_group_id: string | null;
  meta_title: string | null;
  question: string | null;
  at_a_glance: string | null;
  key_takeaways: string[];
  faq: ApiBlogFaqItem[];
  sources: ApiBlogSource[];
  tags: string[];
  entities: string[];
  related_slugs: string[];
};

export type ApiBlogCategory = {
  id: string;
  slug: string;
  name: string;
  description: string;
  meta_title: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * The control-plane `details` blob, of which this client reads exactly one
 * field. The blob is written by the owner-hub control plane at casino
 * creation and stored losslessly by spark-blog (`defs.TenantDetails`), so it
 * also carries `country`, `tokens`, `logos`, `domains` and more — none of
 * which the studio needs.
 *
 * `languages` is the casino's own supported set (e.g. `["mx", "en"]` for a
 * Mexican brand), in the platform's language codes and with the market's
 * primary language first. It is the SAME vocabulary the articles already
 * store in `language`, so no code mapping is needed on the way in.
 */
export type ApiBlogTenantDetails = {
  languages?: string[] | null;
};

/**
 * `GET /tenant/{id}`: the brand's blog tenant. Only the three flags the studio
 * acts on plus the languages out of the control-plane `details` blob are
 * typed; the row also carries name, domains and palette, which this client
 * does not read.
 */
export type ApiBlogTenant = {
  id: string;
  name: string;
  enabled: boolean;
  banned: boolean;
  last_enable_time: string | null;
  details?: ApiBlogTenantDetails | null;
};

// ── Mappers ─────────────────────────────────────────────────────────────────

/**
 * The byline, both ways.
 *
 * A blob with no name maps to no author at all: the public page treats a
 * nameless author as absent (an avatar and a bio with nobody attached is a
 * broken card, not an EEAT signal), and the editor should show the same thing
 * the reader will get.
 */
export const authorFromApi = (author: ApiBlogAuthor | null | undefined): BlogAuthor | undefined => {
  if (!author || !author.name) return undefined;
  return {
    name: author.name,
    role: author.role ?? '',
    bio: author.bio ?? '',
    avatarUrl: author.avatar_url ?? '',
    url: author.url ?? '',
    links: (author.links ?? []).map((link) => ({ label: link.label, url: link.url })),
  };
};

export const authorToApi = (author: BlogAuthor | undefined): ApiBlogAuthor | null => {
  if (!author || !author.name.trim()) return null;
  return {
    name: author.name,
    role: author.role,
    bio: author.bio,
    avatar_url: author.avatarUrl,
    url: author.url,
    // A half-filled row (a label with no address) would render as a link to
    // nowhere, so it is dropped here rather than on the page.
    links: author.links.filter((link) => link.url.trim() !== ''),
  };
};

export const articleFromApi = (article: ApiBlogArticle): BlogArticle => ({
  id: article.id,
  slug: article.slug,
  title: article.title,
  // The model's scalars are non-optional, and the wire omits every zero value
  // (see ApiBlogArticle): a missing string is '', a missing pillar is false, a
  // missing sequence is 0. The pages call string methods on these at render, so
  // an `undefined` here is the 500 page, not a blank field.
  description: article.description ?? '',
  status: article.status,
  language: article.language,
  categoryId: article.category_id ?? '',
  pillar: article.pillar ?? false,
  sequence: article.sequence ?? 0,
  bodyMarkdown: article.body_markdown ?? '',
  hero: article.hero ? { url: article.hero.url, alt: article.hero.alt, caption: article.hero.caption } : undefined,
  author: authorFromApi(article.author),
  translationGroupId: article.translation_group_id ?? undefined,
  metaTitle: article.meta_title ?? undefined,
  question: article.question ?? undefined,
  atAGlance: article.at_a_glance ?? undefined,
  // The TS model's array fields are non-optional: a missing/null wire array is [].
  keyTakeaways: article.key_takeaways ?? [],
  faq: (article.faq ?? []).map((item) => ({ q: item.q, a: item.a })),
  sources: (article.sources ?? []).map((source) => ({ title: source.title, url: source.url })),
  tags: article.tags ?? [],
  entities: article.entities ?? [],
  relatedSlugs: article.related_slugs ?? [],
  updatedAt: article.updated_at,
  publishedAt: article.published_at ?? undefined,
  // Only the one value the service can stamp is read as such; anything else
  // (absent on an older build, or a value this client does not know) is the
  // studio, which is what every article was before Autopilot existed.
  origin: article.origin === 'autopilot' ? 'autopilot' : 'studio',
});

export const articleDraftToApi = (draft: BlogArticleDraft): ApiBlogArticleDraft => ({
  slug: draft.slug,
  title: draft.title,
  description: draft.description,
  language: draft.language,
  category_id: draft.categoryId || null,
  pillar: draft.pillar,
  sequence: draft.sequence,
  body_markdown: draft.bodyMarkdown,
  hero: draft.hero ? { url: draft.hero.url, alt: draft.hero.alt, caption: draft.hero.caption } : null,
  author: authorToApi(draft.author),
  translation_group_id: draft.translationGroupId || null,
  meta_title: draft.metaTitle ?? null,
  question: draft.question ?? null,
  at_a_glance: draft.atAGlance ?? null,
  key_takeaways: draft.keyTakeaways,
  faq: draft.faq.map((item) => ({ q: item.q, a: item.a })),
  sources: draft.sources.map((source) => ({ title: source.title, url: source.url })),
  tags: draft.tags,
  entities: draft.entities,
  related_slugs: draft.relatedSlugs,
});

export const categoryFromApi = (category: ApiBlogCategory): BlogCategory => ({
  id: category.id,
  slug: category.slug,
  name: category.name,
  description: category.description,
});

export const categoryToApi = (
  draft: Omit<BlogCategory, 'id'> & { id?: string },
): Pick<ApiBlogCategory, 'slug' | 'name' | 'description'> => ({
  slug: draft.slug,
  name: draft.name,
  description: draft.description,
});

/**
 * Whether the brand's blog is switched on, as the tenant row reports it.
 *
 * `provisioned: false` is the `404 "Tenant not found"` case: the control plane
 * has never created a tenant for this brand, which is the same thing as "the
 * blog was never turned on". It is kept apart from `enabled` because only one
 * of the two can be fixed by the owner's own switch, and because a 404 must
 * never be confused with a tenant that exists and is merely off.
 */
export type BlogTenantStatus = {
  provisioned: boolean;
  enabled: boolean;
  /** A banned tenant is force-disabled and refuses every enable with a 409. */
  banned: boolean;
  /**
   * The casino's supported languages, straight out of the control-plane
   * `details` blob and in the brand's own order (primary first). Empty when
   * the row predates the blob, when the control plane sent no languages, or
   * when there is no tenant at all — callers fall back rather than treating
   * empty as "this casino supports nothing".
   */
  languages: string[];
};

/** The state a brand with no tenant row is in. */
export const BLOG_TENANT_UNPROVISIONED: BlogTenantStatus = {
  provisioned: false,
  enabled: false,
  banned: false,
  languages: [],
};

/**
 * The casino's languages out of the `details` blob, hardened against every
 * shape an older row can arrive in: a missing blob, a null `languages`, a
 * non-array, or an array with blanks and duplicates in it. Codes are trimmed
 * and lowercased to match what the editor stores on an article; order is the
 * control plane's (the market's primary language first) and is preserved.
 */
export const tenantLanguagesFromApi = (tenant: ApiBlogTenant): string[] => {
  const raw = tenant.details?.languages;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const code = entry.trim().toLowerCase();
    if (code.length === 0 || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
};

export const tenantStatusFromApi = (tenant: ApiBlogTenant): BlogTenantStatus => ({
  provisioned: true,
  // Defensive booleans: an older row (or a field the service stops sending)
  // must read as "off", never as a truthy string that shows the blog as live.
  enabled: tenant.enabled === true,
  banned: tenant.banned === true,
  languages: tenantLanguagesFromApi(tenant),
});

// ── Request context + HTTP ──────────────────────────────────────────────────

/** Tenant scoping style: the tenant lives in the path (`/tenants/{tenant_id}/...`).
 *  Kept as a const so switching to another style is a one-line change here. */
export const TENANT_IN_PATH = true;

/**
 * A request that never left the client: no base URL, no session, no brand.
 *
 * Tagged so the error mappers can tell it apart from a transport failure and
 * show its message verbatim — `new Error('…')` alone is indistinguishable from
 * an axios `Error('Request failed with status code 403')`, which must never
 * reach the owner.
 */
export class BlogContextError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BlogContextError';
  }
}

/**
 * The blog service refused a generation with a 402: the tenant owner's
 * AI-credits wallet cannot cover it. Carries the shortfall the studio primes
 * the Buy-credits sheet with — the same `{ok:false, needed}` contract
 * spark-creatives answers, read here as a typed error because the blog API
 * layer reports refusals by throwing rather than by widening return shapes.
 */
export class BlogInsufficientCreditsError extends Error {
  needed?: number;

  constructor(needed?: number) {
    super('Not enough AI credits for this generation');
    this.name = 'BlogInsufficientCreditsError';
    this.needed = needed;
  }
}

/** The 402 shortfall off any error this API layer threw, else undefined. */
export const blogNeededCreditsOf = (err: unknown): number | undefined =>
  err instanceof BlogInsufficientCreditsError ? err.needed : undefined;

/** Whether an error this API layer threw is the 402 refusal — true even when
 *  the response carried no readable shortfall, so the remediation (the
 *  Buy-credits sheet) never degrades into a dead-end toast over a formatting
 *  detail. */
export const blogIsInsufficientCredits = (err: unknown): boolean =>
  err instanceof BlogInsufficientCreditsError;

/**
 * Accept 402 alongside 2xx so an unaffordable generation resolves into a typed
 * refusal instead of a generic axios throw; every other status still throws.
 */
const acceptPaymentRequired = (status: number): boolean => (status >= 200 && status < 300) || status === 402;

/** Throw the typed 402 when that is what `response` is; otherwise pass it through. */
const throwIfPaymentRequired = (response: { status: number; data?: unknown }): void => {
  if (response.status !== 402) return;
  const needed = (response.data as { needed?: unknown } | undefined)?.needed;
  throw new BlogInsufficientCreditsError(typeof needed === 'number' && needed > 0 ? needed : undefined);
};

export type BlogRequestContext = {
  /** `{blogApiUrl}/tenants/{tenant_id}` — thunk paths (`/articles`, ...) append to it. */
  baseUrl: string;
  /** The owner's access token, sent RAW (see the module header). */
  token: string;
  /**
   * The resolved tenant UUID on its own. It doubles as the brand's casino
   * instance id (they are the same entity), so the owner-hub switch takes it
   * verbatim as `casino_instance_id`, which keeps a pinned `blogTenantId`
   * pointing the read and the write at the SAME tenant instead of splitting
   * them across two brands.
   */
  tenant: string;
  /** `{blogApiUrl}/tenant/{tenant_id}`: the tenant row itself, singular path. */
  tenantUrl: string;
};

/**
 * Authorization header for every blog call. The token is the header value on
 * its own — see "The token goes RAW" in the module header for why a `Bearer `
 * prefix would break the owner-hub lookup.
 */
export const blogAuthHeaders = (token: string): { Authorization: string } => ({ Authorization: token });

/**
 * Resolve base URL + token + tenant for one request.
 *
 * The token is the signed-in owner's access token, with
 * `window.config.blogApiToken` as a dev override (empty in production). The
 * tenant is the `blogTenantId` config override when set, else the active
 * brand's id — which only works where the brand id IS the tenant UUID. Throws
 * (with the copy the blog pages surface through `t()`) when unconfigured,
 * signed out or unscoped.
 */
export const blogRequestContext = (state: RootState): BlogRequestContext => {
  const { blogApiUrl, blogApiToken } = window.config;
  if (!blogApiUrl) throw new BlogContextError('The blog service is not configured');
  // The override first so a dev can pin another owner's (or an admin's) token
  // without logging out of the studio.
  const token = blogApiToken || state.authentication.access_token;
  if (!token) throw new BlogContextError('Sign in again to manage the blog');
  const tenant = window.config.blogTenantId ?? state.casinoInstance.activeBrand?.id;
  if (!tenant) throw new BlogContextError('No brand selected');
  const baseUrl = TENANT_IN_PATH ? `${blogApiUrl}/tenants/${encodeURIComponent(tenant)}` : blogApiUrl;
  return { baseUrl, token, tenant, tenantUrl: `${blogApiUrl}/tenant/${encodeURIComponent(tenant)}` };
};

export const blogRequest = async <T>(
  ctx: BlogRequestContext,
  method: 'get' | 'post' | 'put' | 'delete',
  path: string,
  body?: unknown,
): Promise<T> => {
  const response = await axios.request<T>({
    method,
    url: `${ctx.baseUrl}${path}`,
    data: body,
    headers: blogAuthHeaders(ctx.token),
    // Kept even though the blog now shares the owner-hub session token: its
    // 401 is ambiguous (a broken owner-hub resolver on the blog side reads the
    // same as an expired token) and its 403 — "You do not have access to this
    // tenant" — is a perfectly healthy session hitting someone else's tenant.
    // Without this flag the global interceptor would rotate the owner-hub token
    // and, failing that, log the user out of the studio (see utils/auth-interceptor).
    _owsNoAuthRecovery: true,
  });
  return response.data;
};

/**
 * POST .../media — uploads one image and returns its public CDN URL.
 *
 * The file goes through the blog service, never straight to the bucket: the
 * storage credentials are a server-side service account, and the object name
 * (like every slug here) is server-owned. The URL comes back ready to store on
 * the article, so the caller only puts it in the draft.
 *
 * `Content-Type` is deliberately NOT set — the browser has to add the
 * multipart boundary itself, and setting it by hand would leave that off.
 */
export const uploadBlogMedia = async (ctx: BlogRequestContext, file: File): Promise<string> => {
  const body = new FormData();
  body.append('file', file);

  const response = await axios.post<{ url: string }>(`${ctx.baseUrl}/media`, body, {
    headers: blogAuthHeaders(ctx.token),
    _owsNoAuthRecovery: true,
  });

  return response.data.url;
};

// ── Writing an article ──────────────────────────────────────────────────────



export type BlogWritePart = 'article' | 'body' | 'seo' | 'hero';

export type ApiBlogWriteBrief = {
  parts: BlogWritePart[];
  brief?: string;
  title?: string;
  body_markdown?: string;
  language?: string;
  category_id?: string | null;
};

/** Only the asked-for fields come back; everything else is absent. */
export type BlogWriteBrief = {
  parts: BlogWritePart[];
  /** The owner's own words. Needed by `article` and `body`. */
  brief?: string;
  title?: string;
  /** The body as the editor holds it. Needed by `seo`, and by `hero` without a title. */
  bodyMarkdown?: string;
  language?: string;
};

export type ApiBlogQuizBrief = {
  body_markdown: string;
  title?: string;
  language?: string;
  question?: string;
};

/** One quiz, split the way the `::: quiz` fence splits it. */
export type ApiBlogQuizDraft = { question: string; source: string };

export type BlogQuizBrief = {
  /** The article body as the editor currently has it, dialect and all. */
  bodyMarkdown: string;
  title?: string;
  language?: string;
  /** What the quiz being replaced asks, so the writer covers something else. */
  question?: string;
};

export type BlogQuizDraft = { question: string; source: string };

/**
 * POST .../articles/quiz — the blog service writes ONE quiz block and hands it
 * back.
 *
 * Seconds rather than minutes, unlike a whole-article pass, and it
 * stores nothing at all. No article is created or updated, so the studio drops
 * the block into the body it is already editing and saves that body the ordinary
 * way. That is what lets the editor offer it on a draft with unsaved changes,
 * and on one that has never been saved.
 *
 * The body travels whole rather than as an article id for the same reason: the
 * quiz has to be about the text the author is looking at, not about the last
 * text they saved.
 *
 * `source` comes back checked against the dialect (at least two options, exactly
 * one marked right, an explanation), so it goes between the fences verbatim.
 */
export const generateBlogQuiz = async (
  ctx: BlogRequestContext,
  brief: BlogQuizBrief,
  signal?: AbortSignal,
): Promise<BlogQuizDraft> => {
  const body: ApiBlogQuizBrief = {
    body_markdown: brief.bodyMarkdown,
    title: brief.title,
    language: brief.language,
    question: brief.question,
  };

  const response = await axios.post<ApiBlogQuizDraft>(`${ctx.baseUrl}/articles/quiz`, body, {
    headers: blogAuthHeaders(ctx.token),
    signal,
    // A quiz is one text pass, but it is still a model call: the default axios
    // timeout would be the studio's, not the upstream's.
    timeout: 0,
    _owsNoAuthRecovery: true,
    validateStatus: acceptPaymentRequired,
  });
  throwIfPaymentRequired(response);

  return { question: response.data.question, source: response.data.source };
};

// ── The blog switch: read here, written on owner-hub ────────────────────────

/** HTTP status of a failed call, or `null` when it never reached a response. */
const httpStatus = (err: unknown): number | null => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const status = (err as { response?: { status?: unknown } }).response?.status;
    if (typeof status === 'number') return status;
  }
  return null;
};

/**
 * The same status, for callers outside this module.
 *
 * `blogSetEnabled` needs it to tell a 403 (this caller cannot see the tenant at
 * all) from a read that merely failed, which is the difference between a
 * refusal that has to travel and one the thunk can assume its way past.
 */
export const blogHttpStatus = httpStatus;

/**
 * GET `{blogApiUrl}/tenant/{tenant_id}`: is this brand's blog on?
 *
 * A `404` is NOT an error here: the control plane creates the tenant on the
 * first enable, so an absent row simply means the blog has never been turned
 * on, and the switch renders off. Every other failure still throws, because
 * they are genuinely different states the owner must not read as "off": a `403`
 * is someone else's tenant (or an unowned one, which is admin-only), and a
 * transport failure says nothing at all.
 *
 * The route is `Admin`-secured and per-user, so it answers to the tenant's
 * owner without any control-plane credential.
 */
export const fetchBlogTenantStatus = async (ctx: BlogRequestContext): Promise<BlogTenantStatus> => {
  try {
    const response = await axios.get<ApiBlogTenant>(ctx.tenantUrl, {
      headers: blogAuthHeaders(ctx.token),
      // Same reasoning as `blogRequest`: this service's 401/403 must not rotate
      // the owner-hub token or log the owner out of the studio.
      _owsNoAuthRecovery: true,
    });
    return tenantStatusFromApi(response.data);
  } catch (err) {
    if (httpStatus(err) === 404) return BLOG_TENANT_UNPROVISIONED;
    throw err;
  }
};

/** The service's copy for the 404 every tenant-scoped route answers with. */
const TENANT_NOT_FOUND = 'Tenant not found';

/**
 * One list GET, with the tenant's own `404` read as an empty list rather than
 * as a failure.
 *
 * Same rule as {@link fetchBlogTenantStatus}, for the same reason: there is no
 * auto-provisioning, so a brand whose blog has never been turned on has no
 * tenant row and every route under `/tenants/{id}` answers `404 "Tenant not
 * found"`. On the two LIST routes that is the only 404 the SERVICE can produce
 * — article- and category-not-found are raised on the get and update paths
 * only — so an empty blog is the honest reading. A `403` (someone else's
 * tenant) and every 5xx still throw.
 *
 * The service's copy is checked alongside the status because not every 404
 * comes from the service: a `blogApiUrl` pointing at nothing, or an ingress
 * that does not route this prefix, answers one too. An owner told their blog
 * is empty because the base URL is wrong has nothing to report; one shown the
 * load error does.
 *
 * It asks {@link errorEnvelopeMessage} rather than {@link serverErrorMessage}
 * on purpose: the tenant 404 is `{"error": "Tenant not found"}` specifically,
 * and the swagger-envelope arm must never be able to turn some other 404 into
 * an empty blog.
 */
const listOrEmpty = async <T>(ctx: BlogRequestContext, path: string): Promise<T[]> => {
  try {
    return await blogRequest<T[]>(ctx, 'get', path);
  } catch (err) {
    if (httpStatus(err) === 404 && errorEnvelopeMessage(err) === TENANT_NOT_FOUND) return [];
    throw err;
  }
};

/**
 * GET `.../articles` + `.../categories`: everything the blog pages list.
 *
 * The two are caught SEPARATELY rather than around the pair: `Promise.all`
 * rejects with whichever settles first, so one catch would let a 404 on one
 * call swallow a 500 on the other. The service cannot produce that pair — both
 * calls clear the same tenant check — but an intermediary between them can.
 */
export const fetchBlogContent = async (
  ctx: BlogRequestContext,
): Promise<{ articles: ApiBlogArticle[]; categories: ApiBlogCategory[] }> => {
  const [articles, categories] = await Promise.all([
    listOrEmpty<ApiBlogArticle>(ctx, '/articles'),
    listOrEmpty<ApiBlogCategory>(ctx, '/categories'),
  ]);

  return { articles, categories };
};

/**
 * POST `{userApiUrl}/casino-instance/blog/{enable|disable}`: flip the switch.
 *
 * This is the one call in this module that does NOT go to spark-blog. The
 * `enabled` flag is writable only through spark-blog's static-token
 * `PUT /tenant`, and the studio holds no such token; owner-hub does, and
 * exposes the two verbs keyed by `casino_instance_id` (see the module header).
 * Enabling a brand that has no tenant row yet creates it.
 *
 * A normal owner-hub call, so it keeps the app-wide auth handling (no
 * `_owsNoAuthRecovery`): a 401 from this host really is the studio session.
 *
 * Nothing is read from the response. The state that actually landed comes from
 * a fresh {@link fetchBlogTenantStatus} instead, because the control plane can
 * refuse the change on rules this client cannot evaluate: a banned tenant, or
 * the re-enable rate limit (a tenant may only go from off to on once per
 * `min-reenable-interval`, 24h by default).
 */
export const setBlogEnabled = async (ctx: BlogRequestContext, enabled: boolean): Promise<void> => {
  const { userApiUrl } = window.config;
  if (!userApiUrl) throw new BlogContextError('The blog service is not configured');

  await axios.post(
    `${userApiUrl}/casino-instance/blog/${enabled ? 'enable' : 'disable'}`,
    { casino_instance_id: ctx.tenant },
    { headers: blogAuthHeaders(ctx.token) },
  );
};

/** The parsed response body of a failed call, or `undefined` when it never
 *  reached a response (a transport error, a throw before the request). */
const responseBody = (err: unknown): unknown => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    return (err as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
};

/**
 * The service's copy out of the `{ "error": ... }` envelope, or `null`.
 *
 * Business conflicts (409), not-found (404) and — since the per-user auth
 * change — forbidden (403) all arrive this way, with copy that is already a
 * locale key. The 403/404 the studio can now hit are `You do not have access to
 * this tenant` and `Tenant not found`, both new failure modes: content routes
 * used to answer an empty list for an unknown tenant and had no ownership check
 * at all.
 *
 * The nested `{ "error": { "code", "message" } }` arm is owner-hub's envelope,
 * which only {@link setBlogEnabled} can produce. It matters because the copy
 * the control plane forwards from spark-blog is the only explanation the owner
 * gets for a refused switch (a ban, or the re-enable rate limit).
 *
 * Kept separate from {@link serverErrorMessage} because {@link listOrEmpty}
 * must ask about THIS envelope alone: it reads `Tenant not found` as an empty
 * blog, and that reading may never be triggered by a 404 that merely happens to
 * carry the same words in the other envelope below.
 */
const errorEnvelopeMessage = (err: unknown): string | null => {
  const data = responseBody(err);
  if (typeof data === 'object' && data !== null && 'error' in data) {
    const message = (data as { error: unknown }).error;
    if (typeof message === 'string') return ownerFacing(message);
    if (typeof message === 'object' && message !== null && 'message' in message) {
      const nested = (message as { message: unknown }).message;
      if (typeof nested === 'string') return ownerFacing(nested);
    }
  }
  return null;
};

/**
 * The service's own copy for a failed call, or `null` when the failure carries
 * none (a transport error, an error thrown before the request, a body in the
 * OTHER envelope).
 *
 * spark-blog answers 401, 422 and 500 through its swagger envelope,
 * `{ "code": N, "message": "…" }`, rather than the `{ "error": … }` body its
 * 403/404/409 use — and that envelope is read DELIBERATELY as "no copy". Every
 * string in it is written for an operator (`Unauthorized`, `Internal error`,
 * `path /api/spark-blog/v1/tenants/<uuid>/articles was not found`), none is a
 * locale key, so `t()` would render it verbatim and identically in Spanish, and
 * one of them prints the service's internal route and the tenant UUID onto the
 * owner's screen. The honest owner-facing answer for that whole class is the
 * caller's own fallback; the envelope itself is one line down in the network
 * tab for whoever is diagnosing it.
 *
 * What the owner DOES need is entirely in the `{ "error": … }` arm above:
 * `You do not have access to this tenant` and `Tenant not found` are the two
 * failures that trap them, and both are locale keys that are already
 * translated.
 */
const serverErrorMessage = (err: unknown): string | null => errorEnvelopeMessage(err);

/**
 * The copy, or `null` when it is a transport message rather than an
 * explanation.
 *
 * A service can put its OWN failed call into the `error` field: owner-hub
 * answers `500 {"error": "Request failed with status code 422", "errorUUID":
 * ...}` when its downstream request to the blog service is rejected. That
 * string describes a hop the owner cannot see, is no locale key (so `t()`
 * renders it raw), and tells them nothing they can act on. Dropping it lets the
 * caller fall back to its own wording; the `errorUUID` in the same body is what
 * support traces.
 */
const ownerFacing = (message: string): string | null => {
  const transport = /^Request failed with status code \d+$/.test(message) || /^timeout of /.test(message);
  return transport || message === 'Network Error' ? null : message;
};

/**
 * Message for a failed blog MUTATION: the service's copy, else the generic save
 * error. NOT `getErrorMessage` (utils/errors.ts): that would surface raw
 * transport messages the locale files do not carry.
 */
export const blogApiErrorMessage = (err: unknown): string => serverErrorMessage(err) ?? 'The change could not be saved';

/**
 * Message for a failed blog READ. Same server copy, but a {@link
 * BlogContextError} keeps its own message — "Sign in again to manage the blog"
 * is exactly what an owner whose session lapsed needs on the blog page, and it
 * is the load path that shows it. Everything else is the load fallback.
 */
export const blogFetchErrorMessage = (err: unknown): string => {
  const fromServer = serverErrorMessage(err);
  if (fromServer !== null) return fromServer;
  if (err instanceof BlogContextError) return err.message;
  return 'Failed to load the blog';
};

/* ── Generation jobs ──────────────────────────────────────────────────────── */

/** A generation as the service records it, on the wire. */
export type ApiBlogArticleJob = {
  id: string;
  article_id: string;
  tenant_id: string;
  kind: 'article' | 'body' | 'seo' | 'hero';
  status: 'running' | 'done' | 'failed';
  error?: string | null;
  started_at: string;
  finished_at?: string | null;
};

export type BlogArticleJob = {
  id: string;
  articleId: string;
  kind: 'article' | 'body' | 'seo' | 'hero';
  status: 'running' | 'done' | 'failed';
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
};

const jobFromApi = (j: ApiBlogArticleJob): BlogArticleJob => ({
  id: j.id,
  articleId: j.article_id,
  kind: j.kind,
  status: j.status,
  error: j.error ?? null,
  startedAt: j.started_at,
  finishedAt: j.finished_at ?? null,
});

/**
 * Start a generation on an EXISTING article and return at once.
 *
 * The opposite of the blocking write this replaced, in the one respect that
 * decides the whole design: the work outlives the request. There is no long-held
 * connection to abort, so no `timeout: 0` and no signal — leaving the page no
 * longer stops the writing, which is the entire point. What the job produces is
 * written into the article by the service; the studio re-reads the article when
 * the job lands rather than merging a response.
 *
 * ONE AT A TIME per article, refused by the service with a 409 rather than by
 * anything here — a guard in this file protects one browser tab, and two tabs
 * are exactly the case that has to be caught.
 */
export const startBlogArticleJob = async (
  ctx: BlogRequestContext,
  articleId: string,
  brief: BlogWriteBrief,
): Promise<BlogArticleJob> => {
  const body: ApiBlogWriteBrief = {
    parts: brief.parts,
    brief: brief.brief,
    title: brief.title,
    body_markdown: brief.bodyMarkdown,
    language: brief.language,
  };

  const response = await axios.post<ApiBlogArticleJob>(
    `${ctx.baseUrl}/articles/${encodeURIComponent(articleId)}/jobs`,
    body,
    { headers: blogAuthHeaders(ctx.token), _owsNoAuthRecovery: true, validateStatus: acceptPaymentRequired },
  );
  throwIfPaymentRequired(response);

  return jobFromApi(response.data);
};

/**
 * Create the draft and start the whole-article pass that fills it.
 *
 * The draft comes back immediately, already in the list, while the writing
 * happens behind it. That is why the "writing…" row survives a refresh and shows
 * up on another device: it is a real article, not something this tab is holding.
 */
export const startBlogArticleGeneration = async (
  ctx: BlogRequestContext,
  brief: BlogWriteBrief,
): Promise<{ article: BlogArticle; job: BlogArticleJob }> => {
  const body: ApiBlogWriteBrief = {
    parts: ['article'],
    brief: brief.brief,
    title: brief.title,
    body_markdown: brief.bodyMarkdown,
    language: brief.language,
  };

  const response = await axios.post<{ article: ApiBlogArticle; job: ApiBlogArticleJob }>(
    `${ctx.baseUrl}/articles/jobs`,
    body,
    { headers: blogAuthHeaders(ctx.token), _owsNoAuthRecovery: true, validateStatus: acceptPaymentRequired },
  );
  throwIfPaymentRequired(response);

  return {
    article: articleFromApi(response.data.article),
    job: jobFromApi(response.data.job),
  };
};

/**
 * What is being written for an article right now.
 *
 * This is the answer a reopened page, a second tab and another device all read —
 * the state lives on the article, not in whichever browser started the work.
 * Asks for `running` only: an article generated a dozen times would otherwise
 * send a dozen rows on every poll.
 */
export const listBlogArticleJobs = async (
  ctx: BlogRequestContext,
  articleId: string,
  status?: 'running' | 'done' | 'failed',
): Promise<BlogArticleJob[]> => {
  const response = await axios.get<ApiBlogArticleJob[]>(
    `${ctx.baseUrl}/articles/${encodeURIComponent(articleId)}/jobs`,
    {
      headers: blogAuthHeaders(ctx.token),
      params: status ? { status } : undefined,
      _owsNoAuthRecovery: true,
    },
  );

  return (response.data ?? []).map(jobFromApi);
};

/**
 * GET .../jobs — every generation running for the BRAND, newest first.
 *
 * What the blog list asks on load. The per-article call cannot answer it: a page
 * that has just opened knows no article ids worth asking about, and the
 * generations it most needs to show are exactly the ones it did not start —
 * another tab, another device, a session since closed. Article by article would
 * be a request per row on every tick.
 *
 * Always ask for `running`. Unfiltered this returns every generation the brand
 * has ever run, and it is polled on a timer.
 */
export const listBlogTenantJobs = async (
  ctx: BlogRequestContext,
  status?: 'running' | 'done' | 'failed',
): Promise<BlogArticleJob[]> => {
  const response = await axios.get<ApiBlogArticleJob[]>(`${ctx.baseUrl}/jobs`, {
    headers: blogAuthHeaders(ctx.token),
    params: status ? { status } : undefined,
    _owsNoAuthRecovery: true,
  });

  return (response.data ?? []).map(jobFromApi);
};

/* ── Autopilot (automatic article generation) ─────────────────────────────── */

/*
 * The owner-facing side of `docs/auto-generation.md` (spark-blog): one
 * settings document per tenant (`GET`/`PUT .../auto-generation`, `POST
 * .../auto-generation/resume`) and one notices document (`GET .../notices`,
 * `POST .../notices/clear`). All four answer the same document their GET does,
 * so every call here resolves to the mapped document and the slice installs it
 * wholesale.
 *
 * Both GETs treat a 404 as an ANSWER rather than a failure, in two shades that
 * the panel has to tell apart:
 *
 * - the tenant's own `{"error": "Tenant not found"}` is a blog that has never
 *   been turned on. Autopilot cannot run there (there is no owner to charge and
 *   no site to publish to), so it resolves to the defaults with
 *   `available: false`, and the panel is shown disabled with the same "turn
 *   the blog on first" reason the rest of the page gives;
 * - any other 404 is a service build that predates the feature (the route
 *   itself is unknown, answered through the swagger envelope), and resolves to
 *   {@link BLOG_AUTOGEN_UNAVAILABLE}: the panel is hidden entirely. That is
 *   what makes the studio safe to ship ahead of the service.
 */

export type BlogAutogenRunStatus = 'running' | 'published' | 'failed' | 'skipped' | 'credits';

/**
 * `GET .../auto-generation` on the wire. Same Go `omitempty` rule as the
 * article: every zero value is ABSENT, so a disabled switch arrives with no
 * `enabled` at all, an empty `hints` with no `hints`, and the mapper restores
 * the zero value. Only `available` and `enabled` are read as booleans, and
 * defensively: an absent or non-boolean value is `false`.
 */
export type ApiBlogAutogenLastRun = {
  status?: BlogAutogenRunStatus;
  at?: string;
  article_id?: string | null;
};

export type ApiBlogAutogen = {
  available?: boolean;
  enabled?: boolean;
  next_run_at?: string | null;
  /** The cadence in minutes: 2880 (two days) by default, spread by up to 360. */
  interval_minutes?: number;
  jitter_minutes?: number;
  language?: string;
  author?: ApiBlogAuthor | null;
  hints?: string;
  price_per_article?: number;
  credits_needed?: number | null;
  consecutive_credit_failures?: number;
  last_run?: ApiBlogAutogenLastRun | null;
};

/**
 * `PUT .../auto-generation` body: `author` and `hints` are left untouched when
 * absent. The service reads `author: null` as absent too (both arrive as nil),
 * so a stored byline is cleared by sending an author WITHOUT a name, never by
 * sending null.
 */
export type ApiBlogAutogenPatch = {
  enabled: boolean;
  author?: ApiBlogAuthor;
  hints?: string;
};

/**
 * The tenant's most recent Autopilot attempt. The run's operator text never
 * rides the document (it stays on the run row), so there is nothing to show
 * an owner beyond the status, the time and the article.
 */
export type BlogAutogenLastRun = {
  status: BlogAutogenRunStatus;
  at: string;
  articleId: string;
};

/** The settings document, as the studio models it. */
export type BlogAutogenSettings = {
  /**
   * The service can run Autopilot for this tenant: it has an owner, is not
   * banned, and the writer is configured. `false` locks the panel with a
   * reason; it never hides it (that is {@link BLOG_AUTOGEN_UNAVAILABLE}).
   */
  available: boolean;
  /** The owner's switch. */
  enabled: boolean;
  /** When the next run is due; null while disabled. */
  nextRunAt: string | null;
  /** How often a run is due, in minutes: 2880 (two days) unless the service says otherwise. */
  intervalMinutes: number;
  /** The random spread around each run, in minutes. */
  jitterMinutes: number;
  /** The language the articles are written in (the casino's first supported one). */
  language: string;
  /** The stored byline, or undefined when none is stored. */
  author: BlogAuthor | undefined;
  hints: string;
  /** What one article costs, as the service prices it. Null when unpriced. */
  pricePerArticle: number | null;
  /** The open "not enough credits" notice's price, or null when none is open. */
  creditsNeeded: number | null;
  consecutiveCreditFailures: number;
  lastRun: BlogAutogenLastRun | null;
};

/**
 * The endpoint's answer. `supported: false` is the "unavailable" state: the
 * service answered 404 for the route itself, so the feature does not exist on
 * this build and the studio shows nothing for it. Everything else is a real
 * settings document.
 */
export type BlogAutogenReady = { supported: true } & BlogAutogenSettings;
export type BlogAutogen = { supported: false } | BlogAutogenReady;

/** The 404 of an older service build: no Autopilot at all. The panel hides. */
export const BLOG_AUTOGEN_UNAVAILABLE: BlogAutogen = { supported: false };

/**
 * The cadence the contract fixes for a row the service has not written yet: a
 * run every two days, spread by up to six hours. Both are minutes, as the
 * document carries them, so a test environment can run every five.
 */
export const BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES = 2880;
export const BLOG_AUTOGEN_DEFAULT_JITTER_MINUTES = 360;

/**
 * The defaults the service answers for a tenant with no row, used here for a
 * brand with no TENANT: the blog has never been turned on, so there is nothing
 * Autopilot could run against. `available: false` keeps the switch locked;
 * the panel's own reason ordering says why (the blog is off).
 */
export const BLOG_AUTOGEN_NOT_PROVISIONED: BlogAutogenReady = {
  supported: true,
  available: false,
  enabled: false,
  nextRunAt: null,
  intervalMinutes: BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES,
  jitterMinutes: BLOG_AUTOGEN_DEFAULT_JITTER_MINUTES,
  language: '',
  author: undefined,
  hints: '',
  pricePerArticle: null,
  creditsNeeded: null,
  consecutiveCreditFailures: 0,
  lastRun: null,
};

export const isBlogAutogenUnavailable = (autogen: BlogAutogen | null): boolean =>
  autogen !== null && autogen.supported === false;

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/** A positive count, else null: the service answers 0/null for "none". */
const positiveOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

const lastRunFromApi = (run: ApiBlogAutogenLastRun | null | undefined): BlogAutogenLastRun | null => {
  if (!run || !run.status) return null;
  return {
    status: run.status,
    at: run.at ?? '',
    articleId: run.article_id ?? '',
  };
};

export const autogenFromApi = (doc: ApiBlogAutogen): BlogAutogenReady => ({
  supported: true,
  available: doc.available === true,
  enabled: doc.enabled === true,
  nextRunAt: doc.next_run_at ?? null,
  // The cadence the contract fixes, for a row the service has not written yet.
  intervalMinutes: finiteOr(doc.interval_minutes, BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES),
  jitterMinutes: finiteOr(doc.jitter_minutes, BLOG_AUTOGEN_DEFAULT_JITTER_MINUTES),
  language: doc.language ?? '',
  author: authorFromApi(doc.author),
  hints: doc.hints ?? '',
  pricePerArticle: positiveOrNull(doc.price_per_article),
  creditsNeeded: positiveOrNull(doc.credits_needed),
  consecutiveCreditFailures: finiteOr(doc.consecutive_credit_failures, 0),
  lastRun: lastRunFromApi(doc.last_run),
});

/**
 * GET `.../auto-generation`: the Autopilot settings for this brand.
 *
 * The two 404 readings are explained at the top of this section. Everything
 * else throws, because a 403 (someone else's tenant) and a transport failure
 * are not states the panel may render as "off".
 */
export const fetchBlogAutogen = async (ctx: BlogRequestContext): Promise<BlogAutogen> => {
  try {
    return autogenFromApi(await blogRequest<ApiBlogAutogen>(ctx, 'get', '/auto-generation'));
  } catch (err) {
    if (httpStatus(err) === 404) {
      return errorEnvelopeMessage(err) === TENANT_NOT_FOUND ? BLOG_AUTOGEN_NOT_PROVISIONED : BLOG_AUTOGEN_UNAVAILABLE;
    }
    throw err;
  }
};

/**
 * What the studio writes to the switch. `author` is sent only when the caller
 * puts it in the patch: the service leaves an absent author untouched, and
 * only turning ON has a byline to say anything about. A named author (the
 * remembered one, or the owner's profile name: `autopilotByline` in the blog
 * page) goes out as is; `null` means the studio has no byline of its own to
 * send, and then the STORED one (left by an earlier enable) is cleared so the
 * service falls back to the latest signed article's, or none, as the
 * confirmation says. The service reads a null on the wire as absent, so the
 * clearing is a nameless author ({@link NO_BYLINE}). `hints` has no UI yet and
 * is left absent for the same reason.
 */
export type BlogAutogenPatch = {
  enabled: boolean;
  author?: BlogAuthor | null;
  hints?: string;
};

/**
 * The byline that clears the stored one: the service treats a blank name as
 * no byline at all and stores none, where a null would have left the old one
 * in place.
 */
const NO_BYLINE: ApiBlogAuthor = { name: '' };

export const autogenPatchToApi = (patch: BlogAutogenPatch): ApiBlogAutogenPatch => {
  const body: ApiBlogAutogenPatch = { enabled: patch.enabled };
  if ('author' in patch) body.author = authorToApi(patch.author ?? undefined) ?? NO_BYLINE;
  if (patch.hints !== undefined) body.hints = patch.hints;
  return body;
};

/**
 * PUT `.../auto-generation`: flip the switch (and send the byline on the way
 * on). Answers the settings that actually landed. A refused enable is a 409
 * whose `{ "error": … }` copy is a locale key (no owner, banned, or the writer
 * is off), and reaches the caller through {@link blogApiErrorMessage}.
 */
export const setBlogAutogen = async (ctx: BlogRequestContext, patch: BlogAutogenPatch): Promise<BlogAutogen> =>
  autogenFromApi(await blogRequest<ApiBlogAutogen>(ctx, 'put', '/auto-generation', autogenPatchToApi(patch)));

/**
 * POST `.../auto-generation/resume`: after a top-up, ask for the missed article
 * within minutes rather than at the next slot. No body; the service decides
 * whether the minimum interval allows it and answers the settings either way.
 */
export const resumeBlogAutogen = async (ctx: BlogRequestContext): Promise<BlogAutogen> =>
  autogenFromApi(await blogRequest<ApiBlogAutogen>(ctx, 'post', '/auto-generation/resume'));

/* ── Autopilot notices (the Spotlight cards) ──────────────────────────────── */

export type ApiBlogNoticeArticle = {
  id: string;
  title?: string;
  slug?: string;
  category_slug?: string;
  published_at?: string | null;
  public_url?: string;
};

export type ApiBlogNotices = {
  articles?: ApiBlogNoticeArticle[] | null;
  credits?: { needed?: number; since?: string } | null;
};

export type BlogNoticeArticle = {
  id: string;
  title: string;
  slug: string;
  categorySlug: string;
  publishedAt: string;
  /** The article on the casino site; empty when the tenant has no site URL,
   *  and the studio then falls back to its own preview route. */
  publicUrl: string;
};

export type BlogNotices = {
  /** Autopilot articles published since the owner last cleared the list, newest first. */
  articles: BlogNoticeArticle[];
  /** The open "not enough credits" notice, or null. */
  credits: { needed: number; since: string } | null;
};

/** Nothing to show: the answer of a brand with no notices, and of a 404. */
export const BLOG_NOTICES_NONE: BlogNotices = { articles: [], credits: null };

export const noticesFromApi = (doc: ApiBlogNotices): BlogNotices => ({
  articles: (doc.articles ?? [])
    .filter((article): article is ApiBlogNoticeArticle => typeof article?.id === 'string' && article.id !== '')
    .map((article) => ({
      id: article.id,
      title: article.title ?? '',
      slug: article.slug ?? '',
      categorySlug: article.category_slug ?? '',
      publishedAt: article.published_at ?? '',
      publicUrl: article.public_url ?? '',
    })),
  credits:
    doc.credits && typeof doc.credits.needed === 'number' && doc.credits.needed > 0
      ? { needed: doc.credits.needed, since: doc.credits.since ?? '' }
      : null,
});

/**
 * GET `.../notices`: the "N new articles" and "needs credits" notices.
 *
 * A 404 of either shade (no tenant, or an older build) is "nothing to show":
 * unlike the settings, a notice has no disabled state to render, so both
 * resolve to {@link BLOG_NOTICES_NONE} and the cards simply do not appear.
 */
export const fetchBlogNotices = async (ctx: BlogRequestContext): Promise<BlogNotices> => {
  try {
    return noticesFromApi(await blogRequest<ApiBlogNotices>(ctx, 'get', '/notices'));
  } catch (err) {
    if (httpStatus(err) === 404) return BLOG_NOTICES_NONE;
    throw err;
  }
};

/** Which notices to close. Absent means "leave it". */
export type BlogNoticesClear = { articles?: boolean; credits?: boolean };

/**
 * POST `.../notices/clear`: close the article list and/or the credits notice.
 * Idempotent; answers the notices as they stand after the change.
 */
export const clearBlogNotices = async (ctx: BlogRequestContext, which: BlogNoticesClear): Promise<BlogNotices> =>
  noticesFromApi(
    await blogRequest<ApiBlogNotices>(ctx, 'post', '/notices/clear', {
      articles: which.articles === true,
      credits: which.credits === true,
    }),
  );

/**
 * What the open credits notice still asks the owner for, or null when it asks
 * for nothing.
 *
 * The notice is the service's record that a run was REFUSED for credits, and
 * it stays open until a run succeeds, it is cleared, or Autopilot is turned
 * off: the service never learns of a top-up. The studio does know the balance,
 * so the notice only says "paused" while the balance is short of what the next
 * article costs. That is the notice's own price, or the current price when it
 * rose since (the next run charges the price of the day). A balance not known
 * yet takes the service at its word.
 */
export const autopilotCreditsShortfall = ({
  needed,
  balance,
  price,
}: {
  /** The open notice's price, or null when none is open. */
  needed: number | null;
  /** The owner's AI-credits balance, or null until the wallet has loaded. */
  balance: number | null;
  /** What one article costs now, when known. */
  price?: number | null;
}): number | null => {
  if (needed === null) return null;
  const required = Math.max(needed, price ?? 0);
  if (balance === null || !Number.isFinite(balance)) return required;
  return balance >= required ? null : required;
};
